import { t } from '@glory/i18n';
import { Button, Callout, CardPage, EmptyState, SkeletonRows } from '@glory/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { UserX } from 'lucide-react';

import { EmployeeCard } from '../../../features/team/employee-card.tsx';
import { useCrumbs } from '../../../lib/crumbs.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { requirePermissionRoute } from '../../../lib/permissions.ts';

interface EmployeeSearch {
  /** Вкладка карточки: уведомления о документах сотрудника ведут на «Документы», «Задача принята» — на «К выплате». */
  readonly tab?: 'documents' | 'payable' | undefined;
}

export const Route = createFileRoute('/_app/team/$id')({
  beforeLoad: requirePermissionRoute('employees.read'),
  validateSearch: (search: Record<string, unknown>): EmployeeSearch => ({
    tab: search.tab === 'documents' || search.tab === 'payable' ? search.tab : undefined,
  }),
  component: EmployeePage,
});

/** Карточка сотрудника на всю страницу (⌘↵ из панели). Данные — из того же списка команды. */
function EmployeePage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const team = useQuery(orpc.org.team.list.queryOptions());
  const employee = team.data?.items.find((e) => e.id === id);
  const back = () => {
    void navigate({ to: '/team', search: employee ? { employee: id } : {} });
  };
  useCrumbs(employee ? [{ label: employee.name }] : null);

  if (team.isPending) {
    return (
      <div className="px-4 py-5 md:px-6">
        <SkeletonRows rows={6} />
      </div>
    );
  }
  if (team.isError) {
    return (
      <div className="px-4 py-5 md:px-6">
        <Callout>{team.error.message}</Callout>
      </div>
    );
  }
  if (!employee) {
    return (
      <div className="px-4 py-10 md:px-6">
        <EmptyState
          icon={<UserX />}
          title={t('team.notFound')}
          description={t('team.notFoundText')}
          action={<Button onClick={back}>{t('shell.backToList')}</Button>}
        />
      </div>
    );
  }

  return (
    <EmployeeCard employee={employee} today={team.data.today} initialTab={tab ?? 'profile'}>
      {(card) => (
        <CardPage
          title={card.title}
          meta={card.meta}
          tabs={card.tabs}
          actions={card.actions}
          onBack={back}
          backLabel={t('shell.navTeam')}
        >
          {card.body}
        </CardPage>
      )}
    </EmployeeCard>
  );
}
